import React, { useContext, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import DashboardLayout from "../layout/DashboardLayout";
import { AuthContext } from "../auth/AuthContext";

// Right banners (from /src/assets)
import Banner1 from "../assets/Banner_1.jpeg";
import Banner2 from "../assets/Banner_2.jpeg";

export default function Settings() {
  const { user } = useContext(AuthContext);

  const banners = useMemo(
    () => [
      { id: "b1", image: Banner1 },
      { id: "b2", image: Banner2 }
    ],
    []
  );

  const [profile, setProfile] = useState({
    fullName: user?.fullName || "Anjali Singh",
    email: user?.email || "anjali@email.com",
    phone: user?.phone || "+1 (555) 123-4567",
    title: user?.title || "CEO"
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    eventReminders: true,
    communityUpdates: false
  });

  const onChangeProfile = (key) => (e) =>
    setProfile((p) => ({ ...p, [key]: e.target.value }));

  const onToggle = (key) => (e) =>
    setNotifications((n) => ({ ...n, [key]: e.target.checked }));

  return (
    <DashboardLayout title="Dashboard">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 320px" },
          gap: 3
        }}
      >
        {/* LEFT */}
        <Box sx={{ minWidth: 0 }}>
          {/* Header strip */}
          <Card
            elevation={0}
            sx={{
              bgcolor: "action.selected",
              borderColor: "divider"
            }}
          >
            <CardContent sx={{ py: 2.25 }}>
              <Typography sx={{ fontWeight: 900, color: "text.primary" }}>
                Account & Preferences
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 13, color: "text.secondary" }}>
                Manage your personal information, preferences, and notifications.
              </Typography>
            </CardContent>
          </Card>

          {/* Profile Information */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Profile Information</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card elevation={0}>
              <CardContent
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "140px 1fr" },
                  gap: 2.5,
                  alignItems: "start"
                }}
              >
                {/* Avatar */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Avatar
                    src={user?.avatarUrl || ""}
                    sx={{
                      width: 64,
                      height: 64,
                      border: "1px solid",
                      borderColor: "divider"
                    }}
                  >
                    {(profile.fullName || "U")[0]?.toUpperCase()}
                  </Avatar>
                </Box>

                {/* Form */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2
                  }}
                >
                  <TextField
                    label="Full Name"
                    value={profile.fullName}
                    onChange={onChangeProfile("fullName")}
                    fullWidth
                  />
                  <TextField
                    label="Email Address"
                    value={profile.email}
                    onChange={onChangeProfile("email")}
                    fullWidth
                  />
                  <TextField
                    label="Mobile Number"
                    value={profile.phone}
                    onChange={onChangeProfile("phone")}
                    fullWidth
                  />
                  <TextField
                    label="Professional Title"
                    value={profile.title}
                    onChange={onChangeProfile("title")}
                    fullWidth
                  />

                  <Box sx={{ gridColumn: { xs: "auto", md: "1 / span 2" } }}>
                    <Button variant="contained" sx={{ px: 2.4 }}>
                      Save Changes
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Notifications & Communication */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Notifications & Communication</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card elevation={0}>
              <CardContent sx={{ py: 2.2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 2,
                    alignItems: "center",
                    py: 1
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                      Email Notifications
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      Receive updates about events, community activities, and important announcements
                    </Typography>
                  </Box>
                  <Switch checked={notifications.emailNotifications} onChange={onToggle("emailNotifications")} />
                </Box>

                <Divider sx={{ my: 1.4 }} />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 2,
                    alignItems: "center",
                    py: 1
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                      Event Reminders
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      Get notified about upcoming events you’ve registered for
                    </Typography>
                  </Box>
                  <Switch checked={notifications.eventReminders} onChange={onToggle("eventReminders")} />
                </Box>

                <Divider sx={{ my: 1.4 }} />

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: 2,
                    alignItems: "center",
                    py: 1
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 13.5 }}>
                      Community Updates
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                      Stay informed about new members, discussions, and community highlights
                    </Typography>
                  </Box>
                  <Switch checked={notifications.communityUpdates} onChange={onToggle("communityUpdates")} />
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Privacy & Security */}
          <Box sx={{ mt: 3 }}>
            <Box className="sectionHeader">
              <Typography className="sectionHeaderTitle">Privacy & Security</Typography>
              <Box className="sectionHeaderLine" />
            </Box>

            <Card elevation={0}>
              <CardContent sx={{ py: 2.2 }}>
                {[
                  { label: "Change Password", action: "Update" },
                  { label: "Logout from all devices", action: "Logout All" },
                  { label: "Privacy Policy", action: "View Policy" },
                  { label: "Terms of Use", action: "View Terms" }
                ].map((row, idx) => (
                  <Box key={row.label}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        alignItems: "center",
                        py: 1.3
                      }}
                    >
                      <Typography sx={{ fontSize: 13.5, fontWeight: idx === 1 ? 900 : 800 }}>
                        {row.label}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 12.5,
                          fontWeight: 800,
                          color: "primary.main",
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" }
                        }}
                      >
                        {row.action}
                      </Typography>
                    </Box>
                    {idx !== 3 && <Divider sx={{ my: 1.2 }} />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>

          {/* Logout (bottom like screenshot) */}
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 800,
                color: "text.secondary",
                cursor: "pointer",
                width: "fit-content",
                "&:hover": { textDecoration: "underline" }
              }}
            >
              Logout
            </Typography>
          </Box>
        </Box>

        {/* RIGHT banners */}
        <Box sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: 2 }}>
          {banners.map((b) => (
            <Card key={b.id} elevation={0} sx={{ overflow: "hidden", bgcolor: "background.paper" }}>
              <Box sx={{ p: 1.5, bgcolor: "action.hover" }}>
                <Box
                  sx={{
                    height: 320,
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    backgroundImage: `url(${b.image})`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    backgroundSize: "contain"
                  }}
                />
              </Box>

              <Box sx={{ p: 1.5, pt: 1.2 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {}}
                  sx={{
                    borderColor: "primary.main",
                    color: "primary.main",
                    fontWeight: 800,
                    borderRadius: 14,
                    py: 1
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      </Box>
    </DashboardLayout>
  );
}
